@let showsSelected = challenge().isSelected || previewSelected();

@let hintConfig = config().hints;

@let nextStep = challenge().nextStep;
@let hintReward = nextStep.reward;
@let hintAmount =
  nextStep.target - challenge().progress | formatAmount: 'currency' : 2;

@if (shouldDisplayHints() && hintConfig) {
  <div
    class="hints-box"
    data-cy="left_to_spend_per_challenge_hint"
    [style.backgroundColor]="hintConfig.backgroundColor"
  >
    <p
      class="hints-text"
      [style]="{
        color: hintConfig.color,
        fontFamily: hintConfig.fontFamily,
      }"
    >
      {{
        hintConfig.template
          .replace('{variable}', hintReward.toString())
          .replace('{variable}', hintAmount.toString())
      }}
    </p>
    <button
      class="btn exit-btn"
      data-cy="close-hint-box"
      (click)="toggleHintsBox()"
    >
      <img
        [src]="hintConfig.exitIcon.imageUrl"
        [alt]="hintConfig.exitIcon.alternativeText"
      />
    </button>
  </div>
}

<div
  class="card"
  [id]="challenge().offerId"
  [style.boxShadow]="boxShadow()"
  [class.has-hints]="shouldDisplayHints()"
  [style.border]="config().cardStyles.border"
>
  @if (isBisCampaign()) {
    <b2c-expire-date
      data-cy="expire-date"
      [campaignEndDate]="campaign().endDate"
      [campaignStartDate]="campaign().startDate"
    />
  }
  <div
    class="un-card-header"
    [style.minHeight]="config().header.height"
    [style.backgroundColor]="config().header.backgroundColor"
  >
    <!-- Banner -->
    @let hasBanner = challenge().hasBanner();
    @if (hasBanner) {
      <div class="banner">
        @if (loadBannerInPriority()) {
          <img
            fill
            priority
            class="img responsive-width"
            [alt]="config().bannerAlternativeText"
            [ngSrc]="challenge().productScope.bannerPath!"
          />
        } @else {
          <img
            fill
            class="img responsive-width"
            [alt]="config().bannerAlternativeText"
            [ngSrc]="challenge().productScope.bannerPath!"
          />
        }
      </div>
    } @else {
      <!-- Logo -->
      <div class="logo-container">
        <div class="box">
          <img
            [alt]="config().logoAlternativeText"
            class="logo"
            [src]="challenge().productScope.logoPath"
          />
        </div>
      </div>
    }

    @let headerProductCheck = config().footer.productCheckButton;
    @if (
      !showsSelected && config().subHeader.hasInfoButton && headerProductCheck
    ) {
      <button
        b2cElement
        modalType="scanner"
        type="button"
        class="info-button"
        data-cy="check_product_btn"
        (click)="displayDialog(challenge(), title())"
        [title]="headerProductCheck.ariaLabel"
        [attr.aria-label]="headerProductCheck.ariaLabel"
      >
        @let headerIcon = headerProductCheck.icon;
        @if (headerIcon) {
          <img [src]="headerIcon.imageUrl" [alt]="headerIcon.alternativeText" />
        }
        <p class="label">
          {{ headerProductCheck.label }}
        </p>
      </button>
    }
  </div>

  <!-- Sub-header -->
  <div
    class="card-subheader"
    [class.card-subheader-completed]="challenge().isCompleted"
    [style]="{
      color: config().subHeader.color,
      fontFamily: config().subHeader.fontFamily,
      fontSize: config().subHeader.fontSize,
      backgroundColor: challenge().isCompleted
        ? config().subHeader.completedBackgroundColor
        : config().subHeader.backgroundColor,
    }"
  >
    <p
      class="label"
      b2cTooltipIfTruncated
      [b2cTooltipHideDelay]="80"
      [b2cTooltipShowDelay]="120"
      [b2cTooltipPosition]="'above'"
      [b2cTooltip]="title()"
    >
      {{ title() }}
    </p>

    <!-- Product scope additional information -->
    @let scopeInfoButton = config().subHeader.scopeInfoButton;
    @if (scopeInfoButton && hasScopeInfo() && !challenge().isCompleted) {
      <div class="scope-info">
        <b2c-icon
          b2cElement
          [icon]="scopeInfoButton.imageUrl"
          data-cy="scope_info_btn"
          [buttonTitle]="scopeInfoButton.alternativeText"
          (click)="displayScopeInfoDialog(challenge())"
          [disabled]="
            phase() !== CampaignPhase.CURRENT || challenge().isOutOfStock()
          "
        />
      </div>
    }
  </div>

  <div class="card-bottom">
    <!-- Win Card -->
    @if (challenge().isCompleted) {
      <div class="win-section">
        <b2c-win-card [amount]="challenge().maxPotentialReward" />
      </div>
    } @else {
      <div class="in-progress-container" [class.has-selection]="!showsSelected">
        <div class="card-content">
          <!-- Progress Section -->
          @if (showsSelected) {
            <div class="progress-section">
              <b2c-progress-section
                [config]="config()"
                [challenge]="challenge()"
              />
            </div>
          }
        </div>

        <!-- Card Footer / Selection -->
        @if (showsSelected) {
          <!-- Unselect Button. Keyed on the real selection, not the preview:
               there is nothing to unselect on a card still being swiped. -->
          @let showsUnselection = challenge().canBeUnselected(phase());

          <div class="card-footer">
            @if (showsUnselection) {
              <b2c-unselection-button
                [phase]="phase()"
                [challenge]="challenge()"
                class="footer-unselect-challenge"
                [isBisCampaign]="isBisCampaign()"
                [isEventChallenge]="isEventChallenge()"
              />
            } @else {
              <div
                class="total-spent"
                [style]="{
                  color: config().footer.spentAmount.color,
                  borderRadius: config().footer.spentAmount.borderRadius,
                  backgroundColor: config().footer.spentAmount.backgroundColor,
                }"
              >
                <p>
                  {{ config().footer.label }}:
                  <span
                    class="amount-spent"
                    [innerHTML]="
                      challenge().progress | formatAmount: 'currency' | safe
                    "
                  ></span>
                </p>
                @let spentAmountIcon = config().footer.spentAmount.icon;
                @if (spentAmountIcon) {
                  <img
                    [src]="spentAmountIcon.imageUrl"
                    [alt]="spentAmountIcon.alternativeText"
                  />
                }
              </div>
            }

            @let productCheck = config().footer.productCheckButton;
            @if (phase() !== CampaignPhase.COMPLETED && productCheck) {
              <button
                b2cElement
                type="button"
                modalType="scanner"
                class="eligible-products"
                data-cy="check_product_btn"
                (click)="displayDialog(challenge(), title())"
                [attr.aria-label]="productCheck.ariaLabel"
                [title]="productCheck.ariaLabel"
                [style]="{
                  color: productCheck.color,
                  backgroundColor: productCheck.backgroundColor,
                  textDecoration: productCheck.textDecoration,
                  fontFamily: productCheck.fontFamily,
                }"
              >
                @let icon = productCheck.icon;
                @if (icon) {
                  <img [src]="icon.imageUrl" [alt]="icon.alternativeText" />
                }
                <p class="label">
                  {{ productCheck.label }}
                </p>
              </button>
            }
          </div>
        } @else {
          <b2c-selection-button
            b2cElement
            class="selection-section"
            [phase]="phase()"
            [challenge]="challenge()"
            [isBisCampaign]="isBisCampaign()"
            [isEventChallenge]="isEventChallenge()"
          />
        }
      </div>
    }
  </div>
</div>
